import {
  BadRequestError,
  CanCheckIn,
  DemeritType,
  Gym,
  GymTypes,
  HandledError,
  IDemerit,
  NotFoundError,
  ProgramType,
  RemovedReasonType,
  TimeSpan,
  WorkoutType,
} from 'fitbeat.models';
import { INotificationService } from 'fitbeat.notification-service';
import {
  AreaCategory,
  getAreaCategory,
  GymConfigs,
  GymSchedulingConfig,
  isCalisthenicAreaZone,
  isCalisthenicZone,
  isStaticCardioZone,
  isStaticFunctionalZone,
  isStaticZone,
  ProgramsConfig,
  WorkoutPausesConfig,
} from '../../config';
import { BookingsConfig } from '../../config/bookings';
import { UserProfile } from '../../entities/user/userprofile';
import { setBookingsRemovedState } from '../../helpers';
import {
  IGymTimeSlotsRepository,
  IUserProfileRepository,
  IWorkoutRepository,
  IWorkoutWithDisplayInfoRepository,
  WorkoutFilter,
} from '../../persistence';
import { DbGymRepository } from '../../persistence/repositories/db/db-gym-repository';
import { Mapper } from '../../persistence/serialization/mapper';
import { DemeritsManager, IDemeritsManager } from '../demerits';
import { CurrentDayWorkoutSelectionStrategy } from '../workouthydration';
import { WorkoutHydration } from '../workouthydration/workouthydration';
import {
  CurrentWorkoutSelectionManager,
  isHomeBoxingWorkout,
  WorkoutSelectionManager,
} from '../workoutselection';

import { GymState } from '../../entities/gymschedule/gymstate';
import { IGymSchedule } from '../../entities/gymschedule/igymschedule';
import { InMemoryGymSchedule } from '../../entities/gymschedule/inmemorygymschedule';
import { WorkoutSettings } from '../../entities/user/workoutsettings';
import { ExerciseSessionType, UserExerciseSession } from '../../entities/workoutsession/userexercisesession';
import { UserWorkoutSession } from '../../entities/workoutsession/userworkoutsession';

import { MarketingAnalyticsService } from '../marketing-analytics';
import { WorkoutProgramRecommendations } from '../workoutprograms';
import {
  IBaseTimeSlot,
  IBookingForCheckIn, ICanCheckInCheck,
  IGymScheduler,
} from './igymscheduling';
import { ScheduleExercises } from './scheduleexercises';
import { StationAllocationOrderStrategies } from './stationAllocationOrderStrategies';
import { TimeSlices } from './timeslices';
import {
  getActivityTypeForAreaCategory,
} from '../../config/workout-activity-mappings/fitillion-workout-activity-type-mappings';

// Creates a micro-schedule for the gym, and ensures there are no conflicts in the gym while being effecient
// Works by breaking the workout sessions into a schedule for the gym
// and allocating available stations to new workout sessions
//
// TERMS:
// Station - a place in space for a person in gym, e.g. the max station is the max amount of people that can gym at once
// Zone - a small area with 2 stations next to each other, so that people can move through gym together
// Stage - the nth cycle in a users workout, even a pause at a station counts as a stage
// StagePause - we might want to pause at a Zone for more than one stage
// Movements - similar to stage but only increments when moving to a next zone, so its the stage minus the pauses so far
// Cycle Time - a point in time with a specific duration. all cycles durations are fixed and multitudes of 00:00am
// User Workout Session - a single user workout containing all relevant details. This is what is stored in database
// Exercise session - combines a station, a cycle time, a user workout and an exercise:ie the building block of schedule
// GymState - all the exercise sessions for one cycle, ie the state of the gym at a point in time
// GymSchedule - a collection of gym states for a sequence of cycle times, ie the full schedule
// Slot?
// Slice?
// Quarters - A home workout is broken up into 4 quarters
export class GymScheduler implements IGymScheduler {
  private static readonly oneHourInMilliseconds = (1000 * 60 * 60);

  private static readonly stationsPerZone = 2;

  private static calculateAllGymStatesForWorkoutSessions(
    workoutSessions: UserWorkoutSession[],
    amountOfStations: number,
    timeSlices: TimeSlices,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  ): Map<number, GymState> {
    const sessionsAtTimeSlots = new Map<number, GymState>();
    const requestDate = dateOfRequest.getTime();

    workoutSessions.filter((session: UserWorkoutSession) => {
      const phantomed = session.removedAt ? session.removedAt.getTime() <= dateOfRequest.getTime() : false;
      const isProvisionalAndExpired = (
        session.isProvisionalInvite()
        && session.expiresAt
        && session.expiresAt.getTime() <= requestDate
      );
      return (
        !phantomed && !isProvisionalAndExpired
      );
    })
      .forEach((workoutSession: UserWorkoutSession) => {
        const workoutStartCycleTime = workoutSession.startTime;
        let cycleIterator = new Date(workoutStartCycleTime);
        workoutSession.exerciseSessions.forEach((exerciseSession) => {
          let gymStateAtCycle = sessionsAtTimeSlots.get(cycleIterator.getTime());

          if (!(gymStateAtCycle instanceof GymState)) {
            gymStateAtCycle = new GymState(
              cycleIterator,
              timeSlices.getFollowingCycle(cycleIterator),
              this.createEmptySessions(amountOfStations, gym),
            );
            sessionsAtTimeSlots.set(cycleIterator.getTime(), gymStateAtCycle);
          }

          gymStateAtCycle.sessions[exerciseSession.stationNumber] = exerciseSession;
          cycleIterator = timeSlices.getFollowingCycle(cycleIterator);
        });
      });
    return sessionsAtTimeSlots;
  }

  private static calculateGymScheduleForWorkouts(
    workoutSessions: UserWorkoutSession[],
    amountOfStations: number,
    timeSlices: TimeSlices,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  ): IGymSchedule {
    const gymSchedule = new InMemoryGymSchedule();
    gymSchedule.sessionsAtTimeSlots = GymScheduler.calculateAllGymStatesForWorkoutSessions(
      workoutSessions,
      amountOfStations,
      timeSlices,
      gym,
      dateOfRequest,
    );
    return gymSchedule;
  }

  private static createEmptySessions(amountOfStations: number, gym: Gym): UserExerciseSession[] {
    const emptyState = Array<UserExerciseSession>();
    for (let i = 0; i < amountOfStations; i++) {
      emptyState[i] = new UserExerciseSession(
        ExerciseSessionType.Empty,
        i,
        /* parentWorkoutSession */undefined,
        /* workoutStage */ undefined,
        gym,
      );
    }
    return emptyState;
  }

  public readonly workoutRepository: IWorkoutRepository;

  public readonly workoutWithDisplayInfoRepository: IWorkoutWithDisplayInfoRepository | undefined;

  public readonly userProfileRepository: IUserProfileRepository;

  public workoutProgramRecommendations: WorkoutProgramRecommendations;

  // TODO make timeslices part of GymSchedule and keep it up to date there?
  public readonly timeSlices: TimeSlices;

  public workoutSelectionManager: WorkoutSelectionManager;

  public readonly workoutHydration: WorkoutHydration;

  public currentWorkoutSelectionManager: CurrentWorkoutSelectionManager;

  public demeritsManager: IDemeritsManager;

  public stationAllocationOrderStrategy?:
  (a: number, b: number) => number = StationAllocationOrderStrategies.fillUpLeftSideFirst;

  public calisthenicStationAllocationOrderStrategy?:
  (a: [number, number], b: [number, number]) => number = StationAllocationOrderStrategies
      .fillUpCalisthenicThenLeftSideFirst;

  private logger: any;

  private gymTimeSlotsRepository: IGymTimeSlotsRepository;

  private notificationService?: INotificationService;

  private marketingAnalyticsService?: MarketingAnalyticsService;

  constructor(
    cycleTimeInSeconds: number,
    // Note make sure the GymSchedule is up to date when passing it,
    // else we might interprit stale keepalive as downtime
    sessionsAtTimeSlots: IGymSchedule,
    startDateTime: Date,
    workoutRepository: IWorkoutRepository,
    gymSchedulerStartupTime: Date = new Date(), // used for downtime calculation,
    runTimeInHours = 24,
    workoutSelectionManager: WorkoutSelectionManager,
    userProfileRepository: IUserProfileRepository,
    logger: any,
    currentWorkoutSelectionManager: CurrentWorkoutSelectionManager,
    gymTimeSlotsRepository: IGymTimeSlotsRepository,
    workoutProgramRecommendations: WorkoutProgramRecommendations,
    workoutWithDisplayInfoRepository?: IWorkoutWithDisplayInfoRepository,
    notificationService?: INotificationService,
    marketingAnalyticsService?: MarketingAnalyticsService,
  ) {
    this.logger = logger;
    this.currentWorkoutSelectionManager = currentWorkoutSelectionManager;
    this.gymTimeSlotsRepository = gymTimeSlotsRepository;
    this.notificationService = notificationService;
    this.marketingAnalyticsService = marketingAnalyticsService;
    this.workoutProgramRecommendations = workoutProgramRecommendations;

    this.timeSlices = new TimeSlices(startDateTime, cycleTimeInSeconds, runTimeInHours);
    this.workoutSelectionManager = workoutSelectionManager;
    this.workoutRepository = workoutRepository;
    this.userProfileRepository = userProfileRepository;
    this.workoutHydration = new WorkoutHydration(
      this.workoutSelectionManager.exerciseRepository,
      workoutRepository,
      this.userProfileRepository,
      this.logger,
      this.currentWorkoutSelectionManager,
      this.workoutSelectionManager,
    );
    this.workoutWithDisplayInfoRepository = workoutWithDisplayInfoRepository;
    this.demeritsManager = new DemeritsManager(this.userProfileRepository, this.notificationService);
  }

  // Warning: This is very slow, only use for statistical purposes and not for transactional logic
  public async gymSchedule(dateOfRequest: Date = new Date(), gym: Gym): Promise<IGymSchedule> {
    const gymTypeConfig = GymConfigs[gym.type];
    const workouts = await this.workoutRepository.getAll(gym);
    const gymSchedule = GymScheduler.calculateGymScheduleForWorkouts(
      workouts,
      gymTypeConfig.getAmountOfStations(gym),
      this.timeSlices,
      gym,
      dateOfRequest,
    );
    return gymSchedule;
  }

  public async gymScheduleAt(
    from: Date,
    to: Date,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  ): Promise<IGymSchedule> {
    const fromMinusOneHour = new Date(from.getTime() - GymScheduler.oneHourInMilliseconds);
    const workouts = await this.workoutRepository.get(
      gym,
      undefined,
      fromMinusOneHour,
      to,
      undefined,
      dateOfRequest,
    );
    const gymTypeConfig = GymConfigs[gym.type];
    const gymSchedule = GymScheduler.calculateGymScheduleForWorkouts(
      workouts,
      gymTypeConfig.getAmountOfStations(gym),
      this.timeSlices,
      gym,
      dateOfRequest,
    );
    return gymSchedule;
  }

  public async GetSubScheduleWithAllCyclesPopulatedForWorkout(
    workoutId: string,
    dateOfRequest: Date = new Date(),
  )
    : Promise<GymState[]> {
    const subScheduleList = new Array<GymState>();
    const workout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw new Error(`workout not found, workoutId=${workoutId}`);
    }
    const startCycleTime = this.timeSlices.nextSliceClosestToTime(workout.startTime);
    const endCycleTime = this.timeSlices.nextSliceClosestToTime(workout.endTime);
    const workouts = [workout];
    Mapper.setupAllAfterDeserialize(workouts);
    const gym = DbGymRepository.getByName(workout.gymName);
    const gymTypeConfig = GymConfigs[gym.type];
    const amountOfStations = gymTypeConfig.getAmountOfStations(gym);
    const gymSchedule = GymScheduler.calculateGymScheduleForWorkouts(
      workouts,
      amountOfStations,
      this.timeSlices,
      gym,
      dateOfRequest,
    );
    await this.populateAllCycles(startCycleTime, endCycleTime, gymSchedule, subScheduleList, amountOfStations, gym);
    await this.workoutHydration.hydrateGymStateFromWorkoutSelection(
      subScheduleList,
      gym,
      dateOfRequest,
      new CurrentDayWorkoutSelectionStrategy(
        this.currentWorkoutSelectionManager,
        this.workoutSelectionManager,
      ),
    );
    return subScheduleList;
  }

  public async GetSubScheduleWithAllCyclesPopulated(
    subScheduleStart: Date,
    subScheduleEnd: Date,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  )
    : Promise<GymState[]> {
    const startCycleTime = this.timeSlices.nextSliceClosestToTime(subScheduleStart);
    const endCycleTime = this.timeSlices.nextSliceClosestToTime(subScheduleEnd);

    if (startCycleTime instanceof Date && endCycleTime instanceof Date) {
      const subScheduleList = new Array<GymState>();

      let workouts: UserWorkoutSession[];
      const startOfDayInGym = TimeSpan.createStartOfDayDateInTimezone(subScheduleStart, gym.timezone);
      const endOfDayInGym = TimeSpan.createEndOfDayDateInTimezone(subScheduleEnd, gym.timezone);
      if (this.workoutWithDisplayInfoRepository) {
        workouts = await this.workoutWithDisplayInfoRepository.get(
          gym,
          startOfDayInGym,
          endOfDayInGym,
        );
      } else {
        workouts = await this.workoutRepository.get(
          gym,
          undefined,
          startOfDayInGym,
          endOfDayInGym,
          undefined,
          dateOfRequest,
        );
      }
      const gymTypeConfig = GymConfigs[gym.type];
      const amountOfStations = gymTypeConfig.getAmountOfStations(gym);
      const gymSchedule = GymScheduler.calculateGymScheduleForWorkouts(
        workouts,
        amountOfStations,
        this.timeSlices,
        gym,
        dateOfRequest,
      );
      await this.populateAllCycles(startCycleTime, endCycleTime, gymSchedule, subScheduleList, amountOfStations, gym);
      await this.workoutHydration.hydrateGymStateFromWorkoutSelection(
        subScheduleList,
        gym,
        dateOfRequest,
        new CurrentDayWorkoutSelectionStrategy(
          this.currentWorkoutSelectionManager,
          this.workoutSelectionManager,
        ),
      );
      return subScheduleList;
    }
    throw (new HandledError('Start or end time of requested sub schedule not within the current timeslice'));
  }

  public async SchedulePairSessionNow(
    workoutSessionLeft: UserWorkoutSession,
    workoutSessionRight: UserWorkoutSession,
    dateOfRequest = new Date(),
    gymSchedule?: IGymSchedule,
    preferredStationIndex?: number,
    staticZoneNumber?: number,
  )
    : Promise<boolean> {
    if (!gymSchedule) {
      gymSchedule = await this.getScheduleAroundTheTimeOfSessionToBeScheduled(workoutSessionLeft, dateOfRequest);
    }

    const gym = DbGymRepository.getByName(workoutSessionLeft.gymName);
    const gymTypeConfig = GymConfigs[gym.type];
    const pauseAtStages = this.getStagesWhereUserWillStayInSameStationOnNextCycle(workoutSessionLeft, gym);
    const freeSlot = await this.findFreeSlotForTime(
      gymSchedule,
      workoutSessionLeft.earliestAllowedStartTime,
      workoutSessionLeft.exerciseSessions.length,
      gymTypeConfig.getAmountOfStations(gym),
      gym,
      preferredStationIndex,
      true,
      pauseAtStages,
      workoutSessionLeft.uniqueReference,
      staticZoneNumber,
    );
    if (!freeSlot) {
      return false;
    }

    const [firstFreeCycleTime, firstFreeStationNumberLeft] = freeSlot as [Date, number];
    const firstFreeStationNumberRight = firstFreeStationNumberLeft + 1;
    this.pairSessions(workoutSessionLeft, workoutSessionRight);
    await this.ScheduleSession(
      gymSchedule,
      firstFreeCycleTime,
      workoutSessionLeft,
      firstFreeStationNumberLeft,
      staticZoneNumber,
    );
    await this.ScheduleSession(
      gymSchedule,
      firstFreeCycleTime,
      workoutSessionRight,
      firstFreeStationNumberRight,
      staticZoneNumber,
    );
    await this.workoutRepository.addOrUpdate(workoutSessionLeft);
    await this.workoutRepository.addOrUpdate(workoutSessionRight);
    return true;
  }

  public async QueuePairSession(
    workoutSessionLeft: UserWorkoutSession,
    workoutSessionRight: UserWorkoutSession,
    gym: Gym,
  )
    : Promise<boolean> {
    workoutSessionLeft.gymName = gym.name;
    workoutSessionRight.gymName = gym.name;
    const gymSchedule = await this.getScheduleAroundTheTimeOfSessionToBeScheduled(workoutSessionLeft);

    if (!this.timeSlices.nextSliceClosestToTime(workoutSessionLeft.earliestAllowedStartTime)) {
      return false;
    }
    const gymTypeConfig = GymConfigs[gym.type];
    const pauseAtStages = this.getStagesWhereUserWillStayInSameStationOnNextCycle(workoutSessionLeft, gym);
    const freeSlot: [Date, number] | undefined = await this.findFirstFreeSlotForRequestedTime(
      gymSchedule,
      workoutSessionLeft.earliestAllowedStartTime,
      workoutSessionLeft.exerciseSessions.length,
      gymTypeConfig.getAmountOfStations(gym),
      gym,
      NaN,
      true,
      pauseAtStages,
    );

    if (!freeSlot) {
      return false;
    }

    const [firstFreeCycleTime, firstFreeStationNumberLeft] = freeSlot as [Date, number];

    const firstFreeStationNumberRight = firstFreeStationNumberLeft + 1;
    this.pairSessions(workoutSessionLeft, workoutSessionRight);
    await this.ScheduleSession(gymSchedule, firstFreeCycleTime, workoutSessionLeft, firstFreeStationNumberLeft);
    await this.ScheduleSession(gymSchedule, firstFreeCycleTime, workoutSessionRight, firstFreeStationNumberRight);
    await this.workoutRepository.addOrUpdate(workoutSessionLeft);
    await this.workoutRepository.addOrUpdate(workoutSessionRight);
    return true;
  }

  public async ScheduleSessionNow(
    workoutsession: UserWorkoutSession,
    preferredStationIndex = NaN,
    dateOfRequest = new Date(),
    gymSchedule?: IGymSchedule,
    staticZoneType?: number,
  ): Promise<boolean> {
    if (!gymSchedule) {
      gymSchedule = await this.getScheduleAroundTheTimeOfSessionToBeScheduled(workoutsession, dateOfRequest);
    }

    const gym = DbGymRepository.getByName(workoutsession.gymName);
    const gymTypeConfig = GymConfigs[gym.type];
    const pauseAtStages = this.getStagesWhereUserWillStayInSameStationOnNextCycle(workoutsession, gym);
    const freeSlot = await this.findFreeSlotForTime(
      gymSchedule,
      workoutsession.earliestAllowedStartTime,
      workoutsession.exerciseSessions.length,
      gymTypeConfig.getAmountOfStations(gym),
      gym,
      preferredStationIndex,
      false,
      pauseAtStages,
      undefined,
      staticZoneType,
    );

    if (!freeSlot) {
      return false;
    }

    const [firstFreeCycleTime, firstFreeStationNumber] = freeSlot as [Date, number];

    await this.ScheduleSession(
      gymSchedule,
      firstFreeCycleTime,
      workoutsession,
      firstFreeStationNumber,
      staticZoneType,
    );

    await this.workoutRepository.add(workoutsession);
    return true;
  }

  public async QueueSession(
    workoutsession: UserWorkoutSession,
    gym: Gym,
    preferredStationIndex = NaN,
    staticZoneType?: number,
  ): Promise<boolean> {
    workoutsession.gymName = gym.name;
    const gymSchedule = await this.getScheduleAroundTheTimeOfSessionToBeScheduled(workoutsession);

    if (!this.timeSlices.nextSliceClosestToTime(workoutsession.earliestAllowedStartTime)) {
      return false;
    }
    const gymTypeConfig = GymConfigs[gym.type];
    const pauseAtStages = this.getStagesWhereUserWillStayInSameStationOnNextCycle(workoutsession, gym);
    const freeSlot = await this.findFirstFreeSlotForRequestedTime(
      gymSchedule,
      workoutsession.earliestAllowedStartTime,
      workoutsession.exerciseSessions.length,
      gymTypeConfig.getAmountOfStations(gym),
      gym,
      preferredStationIndex,
      false,
      pauseAtStages,
      undefined,
      staticZoneType,
    );

    if (!freeSlot) {
      return false;
    }

    const [firstFreeCycleTime, firstFreeStationNumber] = freeSlot as [Date, number];

    await this.ScheduleSession(gymSchedule, firstFreeCycleTime, workoutsession, firstFreeStationNumber, staticZoneType);

    return this.workoutRepository.add(workoutsession)
      .then(() => true);
  }

  public async futureCyclesFromDate(
    gym: Gym,
    fromDate: Date = new Date(),
    dateOfRequest: Date = new Date(),
  ): Promise<Map<number, GymState>> {
    const workouts = await this.workoutRepository.getAll(gym);
    const gymTypeConfig = GymConfigs[gym.type];
    const gymSchedule = GymScheduler.calculateAllGymStatesForWorkoutSessions(
      workouts,
      gymTypeConfig.getAmountOfStations(gym),
      this.timeSlices,
      gym,
      dateOfRequest,
    );
    const futureCycles = new Map<number, GymState>();
    gymSchedule.forEach((gymState: GymState, timeKey: number) => {
      if (timeKey > fromDate.getTime()) { futureCycles.set(timeKey, gymState); }
    });

    return futureCycles;
  }

  // return the stations that results in a workout schedule
  // that does not clash with non-empty stations in any stage
  public findFreeStations(
    gymSchedule: IGymSchedule,
    gym: Gym,
    cycleTime: Date,
    amountOfStages: number,
    amountOfStations: number,
    findFullZones = false,
    userWillExerciseInNextZoneAfterPause?: boolean,
    staticZoneType?: number,
  )
    : Promise<number[]> {
    const pauses = WorkoutPausesConfig.pausesInWorkout(amountOfStages, staticZoneType);
    const pauseStages = userWillExerciseInNextZoneAfterPause ? [] : pauses.map((p) => p.stage);

    return this.findFreeStationsOrStationsBelongingToSession(
      gymSchedule,
      gym,
      cycleTime,
      amountOfStages,
      findFullZones,
      pauseStages,
      amountOfStations,
      undefined,
      staticZoneType,
    );
  }

  public async cancelSession(
    session: UserWorkoutSession,
    dateOfRequest: Date = new Date(),
    sessionsToPad = 0,
    removedReason: RemovedReasonType = RemovedReasonType.Cancelled,
    removeSessionImmediately = false,
  ): Promise<boolean> {
    let result = false;
    const gym = DbGymRepository.getByName(session.gymName);
    if (removeSessionImmediately) {
      setBookingsRemovedState(session, dateOfRequest, removedReason);
      await this.workoutRepository.update(session);
      result = true;
    } else {
      result = await this.Remove(
        session.exerciseSessions[0].stationNumber,
        session.startTime,
        dateOfRequest,
        sessionsToPad,
        session.uniqueReference,
        gym,
        removedReason,
      );
    }

    if (session.pairedSessionReference) {
      await this.unPairSessions(session);
    }
    return result;
  }

  public async RemoveWorkoutSession(
    requestDate: Date,
    workoutSession: UserWorkoutSession,
    removedReason: RemovedReasonType,
  ): Promise<UserWorkoutSession | undefined> {
    const workoutRemovalRules = {
      [GymTypes.Indoor]: (session: UserWorkoutSession): boolean => {
        const elapsedTimeInMs = requestDate.getTime() - session.startTime.getTime();
        const allowedElapsedTimeInMs = GymSchedulingConfig.checkInGraceInMinutes * 60 * 1000;
        return !(elapsedTimeInMs < allowedElapsedTimeInMs);
      },
      [GymTypes.Home]: (session: UserWorkoutSession): boolean => requestDate.getTime() > session.endTime.getTime(),
    };

    const reReadSession = async (uniqueReference: string) => {
      try {
        const session = await this.workoutRepository.getWorkoutSessionFromId(uniqueReference);
        return session;
      } catch (err) {
        this.logger.warn(`failed to re-read session ${uniqueReference}`, err);
        return undefined;
      }
    };

    const workoutGym = DbGymRepository.getByName(workoutSession.gymName);
    const shouldRemoveWorkoutCheck = workoutRemovalRules[workoutGym.type];
    if (!shouldRemoveWorkoutCheck) {
      this.logger.warn(`no removal rule for ${workoutSession.gymName}`);
      return;
    }
    if (!shouldRemoveWorkoutCheck(workoutSession)) {
      return;
    }

    try {
      const didCancel = await this.cancelSession(
        workoutSession,
        requestDate,
        0,
        removedReason,
        true,
      );
      if (didCancel) {
        const userProfile = await this.getUserProfile(workoutSession.userProfileId);
        if (userProfile && this.marketingAnalyticsService && removedReason === RemovedReasonType.Missed) {
          this.marketingAnalyticsService.logEventInBackground('workout_missed', userProfile);
        }

        if (removedReason === RemovedReasonType.Missed && workoutGym.type !== GymTypes.Home) {
          const demerit: IDemerit = {
            type: DemeritType.NoShow,
            date: requestDate,
            timezone: DbGymRepository.getByName(workoutSession.gymName).timezone,
            sessionTime: workoutSession.startTimeOfFirstExercise,
            weight: 1,
          };
          await this.demeritsManager.addDemerit(workoutSession.userProfileId, demerit);
        }
        return workoutSession;
      }

      this.logger.warn(`cancelSession(${workoutSession.uniqueReference}) returned false`);
      return undefined;
    } catch (err) {
      if (err.status === 409) {
        this.logger.debug(`conflict while removing session ${workoutSession.uniqueReference}. will retry`);
        const updatedSession = await reReadSession(workoutSession.uniqueReference);
        if (updatedSession) {
          await this.RemoveWorkoutSession(requestDate, updatedSession, removedReason);
        }
        return;
      }
      this.logger.warn(`failed to remove session ${workoutSession.uniqueReference}. continuing...`, err);
      return undefined;
    }
  }

  public async RemoveSessionsNotCheckedIn(dateOfRequest: Date): Promise<UserWorkoutSession[]> {
    const currentUncheckedSessions = await this.workoutRepository.getNonCheckedInAndNotRemoveWorkoutsStartedBefore(
      dateOfRequest,
    );

    const missedSessions: (UserWorkoutSession | undefined)[] = await Promise.all(
      currentUncheckedSessions.map((session: UserWorkoutSession) => this.RemoveWorkoutSession(
        dateOfRequest,
        session,
        RemovedReasonType.Missed,
      )),
    );

    const removedSessions: UserWorkoutSession[] = missedSessions
      .filter((session) => session !== undefined) as UserWorkoutSession[];
    return removedSessions;
  }

  public async GetSessionsNotCheckedInToNotify(date: Date): Promise<UserWorkoutSession[]> {
    const checkInNotified = false;
    const currentUncheckedSessions = await this.workoutRepository.getNonCheckedInAndNotRemoveWorkoutsStartedBefore(
      date,
      checkInNotified,
    );

    const nonCheckedInBookings: UserWorkoutSession[] = currentUncheckedSessions
      .filter((session) => session !== undefined) as UserWorkoutSession[];
    return nonCheckedInBookings;
  }

  public CalculateAnonAmountOfStagesInHour(sessionStartTime: Date): number {
    const endOfClassesInHour = new Date(sessionStartTime);
    endOfClassesInHour.setMinutes(58, 0, 0);
    const timeToEndInMs = endOfClassesInHour.getTime() - sessionStartTime.getTime();
    const cyclesLeftInHour = Math.floor(timeToEndInMs / (this.timeSlices.sliceLengthInSeconds * 1000));
    const amountOfStages = Math.min(WorkoutType.MediumStudioWorkout, cyclesLeftInHour);
    return amountOfStages;
  }

  public async CreateAnonymousSession(
    gym: Gym,
    station: number,
    dateOfRequest = new Date(),
    sessionStartTime?: Date,
  ): Promise<UserWorkoutSession> {
    if (!sessionStartTime) {
      sessionStartTime = dateOfRequest;
    }
    const gymTypeConfig = GymConfigs[gym.type];
    const amountOfStations = gymTypeConfig.getAmountOfStations(gym);
    const amountOfStages = this.CalculateAnonAmountOfStagesInHour(sessionStartTime);
    const startTime = this.timeSlices.previousSliceClosestToTime(sessionStartTime)!;
    const workoutSession = new UserWorkoutSession(
      '',
      amountOfStages,
      new WorkoutSettings(GymSchedulingConfig.anonymizeSettings),
      undefined,
      startTime,
      undefined,
      dateOfRequest,
      gym,
    );

    const gymschedule = await this.getScheduleAroundTheTimeOfSessionToBeScheduled(workoutSession, dateOfRequest);
    const isFree = await this.CanStationBeAnonymized(
      startTime,
      station,
      amountOfStations,
      gym,
      gymschedule,
    );
    if (!isFree) {
      throw new HandledError('Cannot turn this slot into an anonymous session');
    }
    await this.ScheduleSession(gymschedule, startTime, workoutSession, station);
    this.anonymizeSession(workoutSession, dateOfRequest);
    await this.workoutRepository.add(workoutSession);
    return workoutSession;
  }

  public async CanStationBeAnonymized(
    startTime: Date,
    stationNumber: number,
    amountOfStations: number,
    gym: Gym,
    gymschedule: IGymSchedule,
  ): Promise<boolean> {
    const amountOfStages = this.CalculateAnonAmountOfStagesInHour(startTime);

    const dummySession = new UserWorkoutSession('', amountOfStages, undefined, 0, startTime);
    const freeSlot = await this.findFreeSlotForTime(
      gymschedule,
      startTime,
      amountOfStages,
      amountOfStations,
      gym,
      stationNumber,
      false,
      dummySession.getPauses(),
    );
    if (!freeSlot) {
      return false;
    }
    const [firstFreeCycleTime, firstFreeStationNumber] = freeSlot as [Date, number];
    if (firstFreeCycleTime.getTime() !== startTime.getTime() || firstFreeStationNumber !== stationNumber) {
      return false;
    }
    return true;
  }

  public getUserProfile(userId: string): Promise<UserProfile | undefined> {
    return this.userProfileRepository.get(userId);
  }

  public searchUserProfile(fullnameOrNicknameOrEmail: string): Promise<UserProfile[]> {
    return this.userProfileRepository.search(fullnameOrNicknameOrEmail);
  }

  public getWorkoutSession(workoutId: string): Promise<UserWorkoutSession | undefined> {
    return this.workoutRepository.getWorkoutSessionFromId(workoutId);
  }

  public getConfirmedBookingsForUser(userId: string, from: Date = new Date(), to?: Date, dateOfRequest?: Date):
  Promise<UserWorkoutSession[]> {
    return this.workoutRepository.get(
      undefined,
      userId,
      from,
      to,
      WorkoutFilter.FilterRemovedAndProvisional(dateOfRequest),
      from,
    );
  }

  public async getBookingDetailForNextCheckIn(
    userId: string,
    now: Date,
    gym: Gym,
  ): Promise<IBookingForCheckIn | undefined> {
    const queryFrom = new Date(now.getTime() - GymScheduler.oneHourInMilliseconds);
    const queryTo = new Date(now.getTime() + GymScheduler.oneHourInMilliseconds);
    const bookings = await this.workoutRepository.get(
      gym,
      userId,
      queryFrom,
      queryTo,
      WorkoutFilter.FilterRemovedAndProvisional(now),
    );

    const findBestSession = (): IBookingForCheckIn | undefined => {
      // if allowed to checkin for any session, then use that session
      for (const detail of details) {
        if (detail.canCheckIn === CanCheckIn.Yes) {
          return detail;
        }
      }

      // fallback to using the first session for the user
      return details[0];
    };

    const details = bookings
      .filter((booking: UserWorkoutSession) =>
        // workout repo returns *active* bookings, we want those *started*
        booking.startTime >= queryFrom && booking.startTime <= queryTo)
      .map((booking: UserWorkoutSession): IBookingForCheckIn => {
        booking.clearPairedSessionDetailsIfPartnerBooking();
        return {
          ...this.getCheckinDataForBooking(booking.startTime, now),
          booking,
        };
      });

    return findBestSession();
  }

  public getCheckinDataForBooking(
    bookingTime: Date,
    now: Date,
  ): ICanCheckInCheck {
    const beforeStartMilliseconds = TimeSpan.millisecondsForMinutes(
      BookingsConfig.maxNumberOfMinutesPriorToExerciseForCheckin,
    );
    const afterStartMilliseconds = TimeSpan.millisecondsForMinutes(
      BookingsConfig.maxNumberOfMinutesAfterExerciseForCheckin,
    );

    const calculateCanCheckIn = (earliestAllowedCheckIn: Date, latestAllowedCheckIn: Date) => {
      if (now < earliestAllowedCheckIn) {
        return CanCheckIn.TooEarly;
      }
      if (now > latestAllowedCheckIn) {
        return CanCheckIn.TooLate;
      }
      return CanCheckIn.Yes;
    };

    const earliestCheckIn = new Date(bookingTime.getTime() - beforeStartMilliseconds);
    const latestCheckIn = new Date(bookingTime.getTime() + afterStartMilliseconds);

    const canCheckIn = calculateCanCheckIn(earliestCheckIn, latestCheckIn);
    return {
      canCheckIn,
      earliestCheckIn,
      latestCheckIn,
    };
  }

  public getInvitesForUser(userId: string, from: Date = new Date(), to?: Date): Promise<UserWorkoutSession[]> {
    return this.workoutRepository.get(undefined, userId, from, to, WorkoutFilter.FilterNonInvites());
  }

  public updateWorkoutSession(workoutSession: UserWorkoutSession): Promise<void> {
    return this.workoutRepository.update(workoutSession);
  }

  public async updateWorkoutSessionSettings(
    workoutId: string,
    uid: string,
    enabledFeatures: any,
    workoutSettings?: WorkoutSettings,
    shouldSetUserDefaultEquipment?: boolean,
  ):
    Promise<void> {
    const [user, workoutSession] = await Promise.all([
      this.getUserProfile(uid),
      this.workoutRepository.getWorkoutSessionFromId(workoutId),
    ]);
    if (!user) {
      throw new NotFoundError('user', uid);
    }
    if (!workoutSession) {
      throw NotFoundError.Workout(workoutId);
    }
    if (uid !== workoutSession.userProfileId) {
      throw new HandledError(`Cannot change workout settings with id ${workoutId
      } for user ${uid} because the user did not make the booking`);
    }
    const settings = workoutSettings ?? ProgramsConfig.fallbackSettingsForGoal(user.longTermGoal, user.skillLevel);
    const gym = DbGymRepository.getByName(workoutSession.gymName);
    const betaBoxingWorkout = workoutSettings && isHomeBoxingWorkout(workoutSettings, gym.type);
    if (betaBoxingWorkout) {
      workoutSession.areaCategory = 'boxing';
    }

    if (workoutSession.areaCategory && workoutSession.areaCategory !== 'circuit') {
      WorkoutProgramRecommendations.forceSettingsBasedOnWorkoutArea(settings, workoutSession.areaCategory);
    } else {
      WorkoutSettings.removeEquipmentFromStudioSettings(settings, gym.type);
      settings.activityType = getActivityTypeForAreaCategory(settings.cardioOrResistance, 'circuit');
    }

    if ((gym.type !== GymTypes.Home && workoutSession.workoutSelectionId) || betaBoxingWorkout) {
      workoutSession.workoutSelectionId = await this.getWorkoutSelectionIdForSettings(
        settings,
        gym,
        uid,
        workoutSession.areaCategory,
      );
    }
    const workoutDate = workoutSession.startTime;
    const defaultToAllRounder = user.longTermGoal ?? ProgramType.GetStrongAndFitGoal;
    const recommendedSettings = await this.workoutProgramRecommendations.GetRecommendedSettingsForDate(
      uid,
      workoutDate,
      enabledFeatures,
      gym,
      defaultToAllRounder,
      new Date(),
    );
    if (!recommendedSettings) {
      throw new Error(`No recommended settings for workout id ${workoutId}`);
    }
    settings.ignoreProgramRecommendations = !WorkoutProgramRecommendations.areSettingsTheSameForRecommendation(
      recommendedSettings,
      settings,
    );
    workoutSession.settings = settings;
    if (shouldSetUserDefaultEquipment) {
      user.defaultEquipment = workoutSession.settings.equipment;
      await this.userProfileRepository.update(user);
    }
    await this.workoutRepository.update(workoutSession);
  }

  public getNotNotifiedExpiredWorkoutSessions(dateToCheckFor: Date, dateOfRequest: Date):
  Promise<UserWorkoutSession[]> {
    return this.workoutRepository.getExpiredAndNotNotifiedAndNotRemovedProvisionalSessions(
      dateToCheckFor,
    );
  }

  // Needs to be an exact cycle time to match a session
  public async Remove(
    stationNumber: number,
    cycleTime: Date,
    dateOfRequest: Date = new Date(),
    cyclesToPad = 0,
    workoutId?: string,
    gym: Gym = DbGymRepository.getTestGym(),
    removedReason?: RemovedReasonType,
  ): Promise<boolean> {
    let workoutSessionToRemove: UserWorkoutSession;
    const impactedScheduleStartTime = cycleTime;
    const impactedScheduleEndTime = new Date(cycleTime.getTime() + GymScheduler.oneHourInMilliseconds);
    const gymSchedule = await this.gymScheduleAt(
      impactedScheduleStartTime,
      impactedScheduleEndTime,
      gym,
      dateOfRequest,
    );
      // make it 10 seconds earlier so that the screens reflect the correct information
    const cyclesToPadInSeconds = cyclesToPad * (this.timeSlices.sliceLengthInSeconds - 10) * 1000;
    const cycle: GymState | undefined = await gymSchedule.getState(cycleTime.getTime());

    if (cycle != null && cycle !== undefined) {
      const selectedUserExerciseSession = cycle.sessions[stationNumber] as UserExerciseSession;
      if (selectedUserExerciseSession != null && selectedUserExerciseSession !== undefined) {
        const { workoutStage } = selectedUserExerciseSession;
        const workoutSessionToMarkAsPhantom = selectedUserExerciseSession.parentWorkoutSession;
        if (!workoutSessionToMarkAsPhantom || workoutSessionToMarkAsPhantom.uniqueReference !== workoutId) {
          if (workoutId) {
            // not in schedule anyway, can just mark as removed
            const session = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
            if (session) {
              setBookingsRemovedState(session, dateOfRequest, removedReason);
              await this.workoutRepository.update(session);
            }
          }
          return false;
        }
        setBookingsRemovedState(
          workoutSessionToMarkAsPhantom,
          cyclesToPadInSeconds
            ? this.timeSlices.nextSliceClosestToTime(new Date(dateOfRequest.getTime() + cyclesToPadInSeconds))!
            : dateOfRequest,
          removedReason,
        );
        workoutSessionToRemove = workoutSessionToMarkAsPhantom;
        let deleteCycleTime = new Date(cycleTime);

        // don't delete the current stage, only from the next onwards
        const firstWorkoutStageToDelete = workoutStage + 1;

        const deleteStage = async (i: number): Promise<void> => {
          deleteCycleTime = this.timeSlices.getFollowingCycle(deleteCycleTime);
          const cycleToDeleteFrom: GymState | undefined = await gymSchedule.getState(deleteCycleTime.getTime());
          const sessionToDelete = workoutSessionToRemove.exerciseSessions[i - 1];
          if (cycleToDeleteFrom) {
            cycleToDeleteFrom.sessions[sessionToDelete.stationNumber] = new UserExerciseSession(
              ExerciseSessionType.Empty,
              sessionToDelete.stationNumber,
            );
          } else {
            throw new Error('cycle to delete not found');
          }
        };

        const deleteAllFollowingStages = async (index: number): Promise<void> => {
          // just >, == should also delete based on original for loop
          if (index > workoutSessionToMarkAsPhantom.exerciseSessions.length) {
            return Promise.resolve();
          }
          await deleteStage(index);
          return deleteAllFollowingStages(index + 1);
        };

        await deleteAllFollowingStages(firstWorkoutStageToDelete);
        await this.workoutRepository.update(workoutSessionToRemove);
        return true;
      }
    }
    return false;
  }

  public async getAllGymSessions(
    gym: Gym,
    sessionStartTime: Date,
    workoutType: WorkoutType,
  ): Promise<UserWorkoutSession[]> {
    const startTime = sessionStartTime;
    const sessions = await this.workoutRepository.getAllSessionsInDateRangeForGym(startTime, startTime, gym);

    return sessions
      .filter((session: UserWorkoutSession) => session.workoutType === workoutType);
  }

  public getUserProfileRepo(): IUserProfileRepository {
    return this.userProfileRepository;
  }

  public getGymTimeSlotsRepo(): IGymTimeSlotsRepository {
    return this.gymTimeSlotsRepository;
  }

  public async getWorkoutSelectionIdForSettings(
    workoutSettings: WorkoutSettings,
    gym: Gym,
    userProfileId: string,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<string> {
    if (isStaticFunctionalZone(areaCategory)) {
      // always using home workouts with ActiveRefNumber for static functional bookings
      return this.currentWorkoutSelectionManager.getCurrentActiveRefNumber(
        GymTypes.Home,
        workoutSettings,
        gym.timezone,
      );
    }

    const userProfile = await this.getUserProfile(userProfileId);
    const usePreviousWorkoutSelection = userProfile?.useLegacyWorkoutSelectionForHome === false;
    if (!usePreviousWorkoutSelection) {
      // using ActiveRefNumber workout selection
      return this.currentWorkoutSelectionManager.getCurrentActiveRefNumber(
        gym.type,
        workoutSettings,
        gym.timezone,
      );
    }

    // using workout selection based on last
    const shouldIncludeUnderscore = false;
    const previousWorkoutSelection = await this.workoutRepository.getLastWorkoutWorkoutSelectionId(
      userProfileId,
      workoutSettings,
      gym.type,
      shouldIncludeUnderscore,
    );
    const dayToUse = previousWorkoutSelection ? previousWorkoutSelection.workoutSelectionIndex : -1;
    const workoutSelection = await this.currentWorkoutSelectionManager.determineNextWorkoutSelection(
      dayToUse,
      gym.type,
      workoutSettings,
    );
    return workoutSelection?.uniqueReference || this.currentWorkoutSelectionManager.getCurrentActiveRefNumber(
      gym.type,
      workoutSettings,
      gym.timezone,
    );
  }

  public startingStation(
    currentStation: number,
    currentTimeOfSession: Date,
    startTimeOfSession: Date,
    amountOfStations: number,
  ): number {
    let currentSessionMinutes = currentTimeOfSession.getMinutes();
    if (currentSessionMinutes % 2 !== 0) {
      currentSessionMinutes = 2 * Math.floor(currentSessionMinutes / 2);
    }

    const stationTimeDifference = currentSessionMinutes - (startTimeOfSession.getMinutes());
    // Each zone has two stations and lasts for two minutes thus for each minute move one station back
    const amountOfStationsToMoveBack = stationTimeDifference % amountOfStations;
    let result = currentStation - amountOfStationsToMoveBack;
    if (result < 0) {
      result = amountOfStations + result;
    }
    return result;
  }

  public getStartingStationForHalfHourSessions(amountOfStations: number): number {
    const durationOfHalfHour = BookingsConfig.workoutTypeDuration(WorkoutType.MediumStudioWorkout)
      + BookingsConfig.halfHourStartTimeDefaultStationOffSet;
    return durationOfHalfHour % amountOfStations;
  }

  public async getStationsThatCanBeAnonymized(date: Date, gym: Gym) {
    const amountOfSessions = this.CalculateAnonAmountOfStagesInHour(date);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59);
    const schedule = await this.gymScheduleAt(date, endOfDay, gym, undefined);
    const gymTypeConfig = GymConfigs[gym.type];
    return this.findFreeStations(
      schedule,
      gym,
      date,
      amountOfSessions,
      gymTypeConfig.getAmountOfStations(gym),
    );
  }

  public buildBaseTimeSlot(startTime: number, workoutType: WorkoutType): IBaseTimeSlot {
    const endTime = startTime + (this.timeSlices.sliceLengthInSeconds * 1000 * workoutType);
    function getScheduleId() {
      const useLegacyIdsUntil = new Date('2025-03-15T13:00:00Z'); // midnight Sunday Mar 16 in Sydney
      if (startTime < useLegacyIdsUntil.getTime()) {
        if (workoutType === WorkoutType.MediumStudioWorkout) {
          return `${startTime}_15`;
        }
        if (workoutType === WorkoutType.FullStudioWorkout) {
          return `${startTime}_25`;
        }
      }
      return `${startTime}_${workoutType}`;
    }
    const scheduleId = getScheduleId();

    return {
      scheduleId,
      workoutType,
      startTime,
      endTime,
    };
  }

  public buildBaseTimeSlotFromScheduleId(scheduleId: string): IBaseTimeSlot {
    const match = scheduleId.match(/^([0-9]{13})_([0-9]+)$/);
    if (!match) {
      throw new BadRequestError('invalid scheduleId');
    }
    function intToWorkoutType(i: number): WorkoutType {
      // legacy: 15 and 25 mean half hour and hour classes
      if (i === 15) {
        return WorkoutType.MediumStudioWorkout;
      }
      if (i === 25) {
        return WorkoutType.FullStudioWorkout;
      }

      const type = i as WorkoutType;
      if (
        type !== WorkoutType.FullStudioWorkout
        && type !== WorkoutType.MediumStudioWorkout
      ) {
        // either failed to map, or not supported
        throw new BadRequestError('invalid scheduleId');
      }
      return type;
    }

    const startTime = parseInt(match[1], 10);
    const workoutType: WorkoutType = intToWorkoutType(parseInt(match[2], 10));
    return this.buildBaseTimeSlot(startTime, workoutType);
  }

  public async getScheduleAroundDate(gymName: string, date: Date) {
    return this.getScheduleAroundTheTimeOfSessionToBeScheduled({ earliestAllowedStartTime: date, gymName });
  }

  private getStagesWhereUserWillStayInSameStationOnNextCycle(workoutSession: UserWorkoutSession, gym: Gym): number[] {
    return gym.usersWillExerciseInNextZoneAfterPause ? [] : workoutSession.getPauses();
  }

  private async populateAllCycles(
    cycleIterator: Date,
    endCycleTime: Date,
    gymSchedule: IGymSchedule,
    subScheduleList: GymState[],
    amountOfStations: number,
    gym: Gym,
  ): Promise<void> {
    if (cycleIterator.getTime() > endCycleTime.getTime()) {
      return Promise.resolve();
    }

    return gymSchedule.getState(cycleIterator.getTime())
      .then(async (gymStateAtCycle) => {
        if (gymStateAtCycle instanceof GymState) {
          subScheduleList.push(gymStateAtCycle);
        } else {
          subScheduleList.push(
            new GymState(
              cycleIterator,
              this.timeSlices.getFollowingCycle(cycleIterator),
              GymScheduler.createEmptySessions(amountOfStations, gym),
            ),
          );
        }
      })
      .then(() => {
        const nextCycleIterator = this.timeSlices.getFollowingCycle(cycleIterator);
        return this.populateAllCycles(
          nextCycleIterator,
          endCycleTime,
          gymSchedule,
          subScheduleList,
          amountOfStations,
          gym,
        );
      });
  }

  private async unPairSessions(session: UserWorkoutSession) {
    if (session.pairedSessionReference == null) {
      return;
    }
    const removedSession = await this.workoutRepository.getWorkoutSessionFromId(session.uniqueReference);
    const pairedSession = await this.workoutRepository.getWorkoutSessionFromId(session.pairedSessionReference);
    if (!removedSession) {
      throw new HandledError(`No workout found with id ${session.uniqueReference}`);
    }
    if (!pairedSession) {
      throw new HandledError(`No workout found with id ${session.pairedSessionReference}`);
    }
    delete removedSession.pairedSessionReference;
    delete removedSession.pairedSessionUserProfileId;
    delete removedSession.pairedSession;
    delete pairedSession.pairedSessionReference;
    delete pairedSession.pairedSessionUserProfileId;
    delete pairedSession.pairedSession;
    await Promise.all([
      this.workoutRepository.update(removedSession),
      this.workoutRepository.update(pairedSession),
    ]);
  }

  private async getScheduleAroundTheTimeOfSessionToBeScheduled(
    workoutsession: { earliestAllowedStartTime: Date, gymName: string },
    dateOfRequest: Date = new Date(),
  ): Promise<IGymSchedule> {
    const from = new Date(workoutsession.earliestAllowedStartTime.getTime() - GymScheduler.oneHourInMilliseconds);
    const to = new Date(workoutsession.earliestAllowedStartTime.getTime() + GymScheduler.oneHourInMilliseconds);
    // only get schedule one hour before and after given time, should be sufficient for algo
    // the time range of the result should be big enough to detect any schedule conflicts
    // but as small as possible to improve performance
    return this.gymScheduleAt(from, to, DbGymRepository.getByName(workoutsession.gymName), dateOfRequest);
  }

  private pairSessions(left: UserWorkoutSession, right: UserWorkoutSession) {
    left.pairedSessionReference = right.uniqueReference;
    right.pairedSessionReference = left.uniqueReference;

    left.pairedSessionUserProfileId = right.userProfileId;
    right.pairedSessionUserProfileId = left.userProfileId;

    left.pairedSession = right;
    right.pairedSession = left;
  }

  // use only if you know that time is a valid timeslice
  private async findFreeSlotForTime(
    gymSchedule: IGymSchedule,
    time: Date,
    amountOfStages: number,
    amountOfStations: number,
    gym: Gym,
    preferredStationIndex = NaN,
    findFullZones = false,
    pauseAtStages: number[],
    sessionReference?: string,
    staticZoneType?: number,
  )
    : Promise<[Date, number] | undefined> {
    return this.findFreeSlotRecursively(
      gymSchedule,
      time,
      amountOfStages,
      amountOfStations,
      gym,
      preferredStationIndex,
      findFullZones,
      pauseAtStages,
      sessionReference,
      staticZoneType,
    );
  }

  private async findFirstFreeSlotForRequestedTime(
    gymSchedule: IGymSchedule,
    preferredTime: Date,
    amountOfStages: number,
    amountOfStations: number,
    gym: Gym,
    preferredStationIndex = NaN,
    findFullZones = false,
    pauseAtStages: number[],
    sessionReference?: string,
    staticZoneType?: number,
  )
    : Promise<[Date, number] | undefined> {
    const earliestAllowedCycleTime = this.timeSlices.nextSliceClosestToTime(preferredTime) as Date;
    return this.findFreeSlotRecursively(
      gymSchedule,
      earliestAllowedCycleTime,
      amountOfStages,
      amountOfStations,
      gym,
      preferredStationIndex,
      findFullZones,
      pauseAtStages,
      sessionReference,
      staticZoneType,
    );
  }

  private async ScheduleSession(
    gymSchedule: IGymSchedule,
    scheduleStartCycle: Date,
    workoutsession: UserWorkoutSession,
    scheduleStartStation: number,
    staticZoneType?: number,
  ): Promise<void> {
    // important: set the actual start and end only at this point
    workoutsession.startTime = scheduleStartCycle;
    workoutsession.startTimeOfFirstExercise = this.startTimeAfterInitialPause(workoutsession, staticZoneType);
    workoutsession.endTime = new Date(workoutsession.startTime.getTime()
        + (workoutsession.exerciseSessions.length * this.timeSlices.sliceLengthInSeconds * 1000));
    workoutsession.currentStage = 0; // 1 is the first stage

    const adjustedStation = this.adjustedStartStationPosition(scheduleStartStation, staticZoneType);
    const oneCycleBeforeStart = this.timeSlices.previousSliceClosestToTime(scheduleStartCycle);

    const {
      movesDueToStaticPosition,
      staticZonePositions,
      movesDueToStaticPositionMultiplier,
    } = await this.calculateZoneMatrix(
      gymSchedule,
      oneCycleBeforeStart,
    );

    if (oneCycleBeforeStart == null) { return; }
    const gym = DbGymRepository.getByName(workoutsession.gymName);
    await this.scheduleNextExerciseStages(
      gymSchedule,
      workoutsession,
      oneCycleBeforeStart,
      adjustedStation,
      gym,
      movesDueToStaticPosition,
      staticZonePositions,
      movesDueToStaticPositionMultiplier,
      staticZoneType,
    );
    ScheduleExercises.setShouldUseAlternativeExercisesInStudio(workoutsession.exerciseSessions, gym);
  }

  private async scheduleNextExerciseStages(
    gymSchedule: IGymSchedule,
    session: UserWorkoutSession,
    lastCycleTime: Date,
    lastStationNumber: number,
    gym: Gym,
    movesDueToStaticPosition: number[],
    staticZonePositions: number[],
    movesDueToStaticPositionMultiplier: number[],
    staticZoneType?: number,
  ): Promise<void> {
    if (session.exerciseSessions.length === (session.currentStage)) {
      // end recursion when amount of stages met
      return;
    }
    const gymTypeConfig = GymConfigs[gym.type];
    const amountOfStations = gymTypeConfig.getAmountOfStations(gym);
    const cycleTime = this.timeSlices.nextSliceClosestToTime(lastCycleTime) as Date;

    const stationNumberIncrementedForCircuit = this.shouldPauseAtThisStage(session.currentStage, session, gym)
      ? lastStationNumber : lastStationNumber + GymScheduler.stationsPerZone;
    const adjustedStation = this.adjustedStationPosition(
      lastStationNumber,
      stationNumberIncrementedForCircuit,
      staticZoneType,
    );

    const { stationNumber, amendedMultiplier } = !staticZoneType
      ? this.calculateCurrentCircuitStation(
        stationNumberIncrementedForCircuit,
        amountOfStations,
        movesDueToStaticPosition,
        staticZonePositions,
        movesDueToStaticPositionMultiplier,
      ) : this.returnStationForStaticZone(lastStationNumber);

    await this.createEmptyStateIfCycleDoesNotExist(cycleTime, gymSchedule, amountOfStations, gym);
    const cycle: GymState | undefined = await gymSchedule.getState(cycleTime.getTime());

    session.currentStage += 1;
    const exerciseSessionToAssign = session.exerciseSessions[session.currentStage - 1];
    UserExerciseSession.setStation(exerciseSessionToAssign, stationNumber, gym);

    // Rule: never override non-empty station:
    // if we made a mistake don't punish the earlier booking
    if (cycle && stationNumber < cycle.sessions.length) {
      const station = cycle.sessions[stationNumber];

      if (station.isEmpty()
        || station.parentWorkoutSession.uniqueReference === session.uniqueReference) {
        cycle.sessions[stationNumber] = exerciseSessionToAssign;
      }
    }

    if (isNaN(exerciseSessionToAssign.zone.typeNumber) || isNaN(exerciseSessionToAssign.zone.numberInGym)) {
      throw Error('Unexpected error: zone should be set when station is set');
    }
    await this.scheduleNextExerciseStages(
      gymSchedule,
      session,
      cycleTime,
      adjustedStation,
      gym,
      movesDueToStaticPosition,
      staticZonePositions,
      amendedMultiplier,
      staticZoneType,
    );
  }

  private loopAroundIfStationIsLargerThanAmountOfStations(
    newStationNumber: number,
    amountOfStations: number,
  ): number {
    if (newStationNumber < 0) {
      newStationNumber = 0;
    }
    return newStationNumber % amountOfStations;
  }

  private calculateCurrentCircuitStation(
    newStationNumber: number,
    amountOfStations: number,
    movesDueToStaticPosition: number[],
    staticZonePositions: number[],
    movesDueToStaticPositionMultiplier: number[],
  ): { stationNumber: number, amendedMultiplier: number[] } {
    const modOfStation = this.loopAroundIfStationIsLargerThanAmountOfStations(
      newStationNumber,
      amountOfStations,
    );
    if (staticZonePositions.length === 0) {
      return { stationNumber: modOfStation, amendedMultiplier: movesDueToStaticPositionMultiplier };
    }
    let currentStation = modOfStation;
    for (let i = 0; i < movesDueToStaticPosition.length; i++) {
      currentStation += movesDueToStaticPosition[i] * movesDueToStaticPositionMultiplier[i];
    }
    let modOfCurrentStation = currentStation % amountOfStations;
    let validStation = false;

    while (!validStation) {
      for (let i = 0; i < staticZonePositions.length; i++) {
        if (modOfCurrentStation >= staticZonePositions[i]
          && modOfCurrentStation < staticZonePositions[i] + movesDueToStaticPosition[i]
        ) {
          modOfCurrentStation += movesDueToStaticPosition[i];
          movesDueToStaticPositionMultiplier[i] = movesDueToStaticPositionMultiplier[i] + 1;
          break;
        }
        if (i === staticZonePositions.length - 1) {
          validStation = true;
        }
      }
      modOfCurrentStation %= amountOfStations;
    }

    return { stationNumber: modOfCurrentStation, amendedMultiplier: movesDueToStaticPositionMultiplier };
  }

  private returnStationForStaticZone(
    stationNumber: number,
  ): { stationNumber: number, amendedMultiplier: number[] } {
    return {
      stationNumber,
      amendedMultiplier: [],
    };
  }

  private shouldPauseAtThisStage(
    currentStage: number,
    session: UserWorkoutSession,
    gym: Gym,
  ): boolean {
    const pauses = session.getPauses();
    if (pauses == null || pauses.length < 1 || gym.usersWillExerciseInNextZoneAfterPause) {
      return false;
    }
    for (const pause of pauses) {
      // pause of the stage after pause so that we sit on same station
      if (currentStage === pause) {
        return true;
      }
    }
    return false;
  }

  private startTimeAfterInitialPause(session: UserWorkoutSession, staticZoneType?: number): Date {
    const workoutHasPause = WorkoutPausesConfig.isFirstStageAPause(session.workoutType, staticZoneType);
    if (workoutHasPause && session.startTime) {
      return this.timeSlices.getFollowingCycle(session.startTime);
    }
    return session.startTime;
  }

  // Look for first slot where subsiquent slots are also available
  // Preferred station would be taken only if available on first available cycle
  private async findFreeSlotRecursively(
    gymSchedule: IGymSchedule,
    cycleTime: Date,
    amountOfStages: number,
    amountOfStations: number,
    gym: Gym,
    preferredStationIndex = NaN,
    findFullZones = false,
    pauseAtStages: number[],
    sessionReference?: string,
    staticZoneType?: number,
  )
    : Promise<[Date, number] | undefined> {
    const nextCycle = await gymSchedule.getState(cycleTime.getTime());
    const doesTheNextGymCycleExitsYet = nextCycle != null;
    const orderedFreeStations: number[] = await this.findFreeStationsOrStationsBelongingToSession(
      gymSchedule,
      gym,
      cycleTime,
      amountOfStages,
      findFullZones,
      pauseAtStages,
      amountOfStations,
      sessionReference,
      staticZoneType,
    );
    const areThereNoFreeStationsInTheCurrentCycle = orderedFreeStations.length === 0;

    if (doesTheNextGymCycleExitsYet && areThereNoFreeStationsInTheCurrentCycle) {
      // recursively search in following cycles. Only useful for non fixed start times
      const timeOfFollowingCycle = this.timeSlices.getFollowingCycle(cycleTime);
      return this.findFreeSlotRecursively(
        gymSchedule,
        timeOfFollowingCycle,
        amountOfStages,
        amountOfStations,
        gym,
        preferredStationIndex,
        findFullZones,
        pauseAtStages,
      );
    }
    if (!doesTheNextGymCycleExitsYet) {
      await this.createEmptyStateIfCycleDoesNotExist(cycleTime, gymSchedule, amountOfStations, gym);
    }
    const firstAllocatedFreeStation = orderedFreeStations[0];
    const preferredStation = this.findNextPreferredStationIndex(orderedFreeStations, preferredStationIndex);
    const finalStationIndex = preferredStation ?? firstAllocatedFreeStation;
    return [cycleTime, finalStationIndex];
  }

  private orderFreeStationsByAllocationStrategy(freeStations: number[][], calisthenicZoneLookup: boolean) {
    if (calisthenicZoneLookup) {
      const calisthenicFreeStationsClone = [...freeStations];
      return calisthenicFreeStationsClone.sort(this.calisthenicStationAllocationOrderStrategy).map((x) => x[0]);
    }
    const freeStationsClone = freeStations.map((x) => x[0]);
    return freeStationsClone.sort(this.stationAllocationOrderStrategy);
  }

  private findNextPreferredStationIndex(
    freeStationsOrderedForAllocation: number[],
    preferredStationNumber: number,
  ): number | undefined {
    let freeStationsOrdered = freeStationsOrderedForAllocation;
    const isThereAPreferredStation = !isNaN(preferredStationNumber);
    if (!isThereAPreferredStation || !preferredStationNumber) {
      return undefined;
    }
    if (freeStationsOrdered.length === 0) {
      return undefined;
    }

    const isPreferredStationFree = freeStationsOrdered.indexOf(preferredStationNumber) !== -1;
    if (isPreferredStationFree) {
      return preferredStationNumber;
    }

    const priorityFreeStationsOrdered = StationAllocationOrderStrategies.getOnlyPriorityStations(
      freeStationsOrdered,
      this.stationAllocationOrderStrategy,
    );
    if (priorityFreeStationsOrdered.length > 0) {
      freeStationsOrdered = priorityFreeStationsOrdered;
    }

    const higherThanPreferred = freeStationsOrdered.filter((s) => s > preferredStationNumber);
    const lowerThanPreferred = freeStationsOrdered.filter((s) => s < preferredStationNumber);
    const orderedStationWithPreferredAsStartingPoint = [...higherThanPreferred, ...lowerThanPreferred];
    const firstAvailableStationAfterPreferredStation = orderedStationWithPreferredAsStartingPoint[0];
    return firstAvailableStationAfterPreferredStation;
  }

  private async isStationFreeAtAmountOfMovementsOrBelongsToSession(
    gymSchedule: IGymSchedule,
    cycleTime: Date,
    amountOfMovements: number,
    station: number,
    amountOfStations: number,
    findFullZones: boolean,
    movesDueToStaticPosition: number[],
    staticZonePositions: number[],
    movesDueToStaticPositionMultiplier: number[],
    sessionReference?: string,
    staticZoneType?: number,
  ): Promise<{ free: boolean, amendedMultiplier: number[] }> {
    if (!cycleTime) {
      return { free: false, amendedMultiplier: [] };
    }

    const cycle: GymState | undefined = await gymSchedule.getState(cycleTime.getTime());
    const { stationNumber, amendedMultiplier } = !staticZoneType
      ? this.calculateCurrentCircuitStation(
        station + (amountOfMovements * GymScheduler.stationsPerZone),
        amountOfStations,
        movesDueToStaticPosition,
        staticZonePositions,
        movesDueToStaticPositionMultiplier,
      ) : this.returnStationForStaticZone(station);

    if (cycle instanceof GymState) {
      if (!cycle.isFreeStation(stationNumber)
          && !cycle.stationBelongsToSpecifiedWorkoutSession(stationNumber, sessionReference)) {
        return { free: false, amendedMultiplier: [] };
      }
      const stationForMovementOnOtherZoneStation = this.loopAroundIfStationIsLargerThanAmountOfStations(
        stationNumber + 1,
        amountOfStations,
      );
      if (findFullZones && !cycle.isFreeStation(stationForMovementOnOtherZoneStation)
          && !cycle.stationBelongsToSpecifiedWorkoutSession(stationForMovementOnOtherZoneStation, sessionReference)) {
        return { free: false, amendedMultiplier: [] };
      }
    }

    return { free: true, amendedMultiplier };
  }

  private movements(stagesSoFar: number, pausesSoFar: number): number {
    return stagesSoFar - pausesSoFar;
  }

  // iterate through all the STAGES on to check ONE STATION to see if it is free
  private async checkIfStationIsFreeOrBelongsToSession(
    station: number,
    currentMovements: number,
    lastMovements: number,
    cycleTimeIterator: Date,
    findFullZones: boolean,
    amountOfStages: number,
    gymSchedule: IGymSchedule,
    amountOfStations: number,
    pauseStages: number[],
    movesDueToStaticPosition: number[],
    staticZonePositions: number[],
    movesDueToStaticPositionMultiplier: number[],
    sessionReference?: string,
    staticZoneType?: number,
  )
    : Promise<boolean> {
    const nextMovement = this.incrementAmountOfMovementsIfNotPause(
      pauseStages,
      currentMovements,
      lastMovements,
    );

    const totalAmountOfMovements = this.movements(amountOfStages, pauseStages.length);
    const haveWeReachedTheLastMovement = currentMovements >= totalAmountOfMovements;
    if (haveWeReachedTheLastMovement) {
      // if we have reached the last movement and there were no non free stations, assume its free and end recursion
      return true;
    }

    const { free, amendedMultiplier } = await this.isStationFreeAtAmountOfMovementsOrBelongsToSession(
      gymSchedule,
      cycleTimeIterator,
      currentMovements,
      station,
      amountOfStations,
      findFullZones,
      movesDueToStaticPosition,
      staticZonePositions,
      movesDueToStaticPositionMultiplier,
      sessionReference,
      staticZoneType,
    );

    if (!free) {
      // no need to continue looking in, we know its not free, so return false and end recursion
      return false;
    }

    // if this movement was free for the station, try the next cycle recursively, ie step through future cycles
    const nextCycleTimeIterator = this.timeSlices.getFollowingCycle(cycleTimeIterator);
    return this.checkIfStationIsFreeOrBelongsToSession(
      station,
      nextMovement,
      currentMovements,
      nextCycleTimeIterator,
      findFullZones,
      amountOfStages,
      gymSchedule,
      amountOfStations,
      pauseStages,
      movesDueToStaticPosition,
      staticZonePositions,
      amendedMultiplier,
      sessionReference,
      staticZoneType,
    );
  }

  private incrementAmountOfMovementsIfNotPause(
    pauseStages: number[],
    currentMovements: number,
    lastMovements: number,
    userWillExerciseInNextZoneAfterPause?: boolean,
  ) {
    const areThereAnyPauses = pauseStages.length > 0;
    let isThisMovementAtAPause = false;
    if (areThereAnyPauses) {
      let pauseCountSoFar = 0;
      for (const pauseStage of pauseStages) {
        if (this.movements(pauseStage - 1, pauseCountSoFar) === currentMovements) {
          isThisMovementAtAPause = true;
        }
        ++pauseCountSoFar;
      }
    }

    const haveWeCheckedThisMovementBefore = currentMovements !== lastMovements;

    const pauseHere = areThereAnyPauses
      && isThisMovementAtAPause
      && haveWeCheckedThisMovementBefore; // we don't want to pause the same place again

    const shouldFreezeZoneForPause = pauseHere && !userWillExerciseInNextZoneAfterPause;
    const nextMovements = shouldFreezeZoneForPause ? currentMovements : currentMovements + 1;
    return nextMovements;
  }

  // return the stations that results in a workout schedule
  // that does not clash with non-empty stations in any stage
  private async findFreeStationsOrStationsBelongingToSession(
    gymSchedule: IGymSchedule,
    gym: Gym,
    cycleTime: Date,
    amountOfStages: number,
    findFullZones = false,
    pauseStages: number[],
    amountOfStations: number,
    sessionReference?: string,
    staticZoneType?: number,
  )
    : Promise<number[]> {
    const freeStationsInCycle = new Array<[number, number]>();
    if (isStaticCardioZone(staticZoneType)) {
      findFullZones = true;
    }

    const {
      movesDueToStaticPosition,
      staticZonePositions,
      movesDueToStaticPositionMultiplier,
    } = await this.calculateZoneMatrix(
      gymSchedule,
      cycleTime,
    );

    const findAllFree = async (station: number): Promise<void> => {
      if (station >= amountOfStations) {
        return;
      }
      const positionInZoneMapping = Math.floor(station / 2);
      const nextStationToCheck = findFullZones ? station + 2 : station + 1;
      const currentZoneInGym = gym.zoneMapping[positionInZoneMapping];
      const stationIsStatic = isStaticZone(currentZoneInGym[0]);
      if (stationIsStatic) {
        const calisthenicOverride = getAreaCategory(staticZoneType) === 'calisthenic'
          && isCalisthenicAreaZone(currentZoneInGym[0]);
        const staticTypeDoesNotMatch = staticZoneType !== currentZoneInGym[0]
          && !calisthenicOverride;
        if (!staticZoneType || staticTypeDoesNotMatch) {
          return findAllFree(nextStationToCheck);
        }
      }
      if (staticZoneType && !stationIsStatic) {
        return findAllFree(nextStationToCheck);
      }

      movesDueToStaticPositionMultiplier.fill(0);

      const startingAmountOfMovements = 0;
      const startingLastAmountOfMovements = startingAmountOfMovements - 1;
      const free = await this.checkIfStationIsFreeOrBelongsToSession(
        station,
        startingAmountOfMovements,
        startingLastAmountOfMovements,
        new Date(cycleTime),
        findFullZones,
        amountOfStages,
        gymSchedule,
        amountOfStations,
        pauseStages,
        movesDueToStaticPosition,
        staticZonePositions,
        movesDueToStaticPositionMultiplier,
        sessionReference,
        staticZoneType,
      );

      const fallbackToCircuit = 1;

      if (free) {
        freeStationsInCycle.push([station, currentZoneInGym[0] ?? fallbackToCircuit]);
      }

      return findAllFree(nextStationToCheck);
    };

    await findAllFree(0);

    const calisthenicZoneLookup = isCalisthenicZone(staticZoneType);
    const orderedFreeStations = this.orderFreeStationsByAllocationStrategy(freeStationsInCycle, calisthenicZoneLookup);
    return orderedFreeStations;
  }

  private async calculateZoneMatrix(
    gymSchedule: IGymSchedule,
    cycleTime: Date,
  ): Promise<{
      movesDueToStaticPosition: number[],
      staticZonePositions: number[],
      movesDueToStaticPositionMultiplier: number[],
    }> {
    const movesDueToStaticPosition: number[] = [];
    const staticZonePositions: number[] = [];
    const cycle: GymState | undefined = await gymSchedule.getState(cycleTime.getTime());
    if (cycle instanceof GymState) {
      let movesAfterStaticPosition = 0;
      let staticZonePosition = 0;
      let lookingForStaticPosition = true;
      for (const session of cycle.sessions) {
        if (session.zone && isStaticZone(session.zone.typeNumber)) {
          if (lookingForStaticPosition) {
            staticZonePositions.push(staticZonePosition);
            lookingForStaticPosition = false;
          }
          movesAfterStaticPosition += 1;
          if (session === cycle.sessions[cycle.sessions.length - 1]) {
            movesDueToStaticPosition.push(movesAfterStaticPosition);
          }
        } else {
          if (!lookingForStaticPosition) {
            movesDueToStaticPosition.push(movesAfterStaticPosition);
            movesAfterStaticPosition = 0;
          }
          lookingForStaticPosition = true;
        }
        staticZonePosition += 1;
      }
    }
    const movesDueToStaticPositionMultiplier = new Array(staticZonePositions.length).fill(0);

    return {
      movesDueToStaticPosition,
      staticZonePositions,
      movesDueToStaticPositionMultiplier,
    };
  }

  private createEmptyStateIfCycleDoesNotExist(
    cycleTime: Date,
    gymSchedule: IGymSchedule,
    amountOfStations: number,
    gym: Gym,
  ): Promise<void> {
    return new Promise((resolve, reject) => { // TODO: fix silliness
      gymSchedule.hasState(cycleTime.getTime())
        .then((hasState: boolean) => {
          if (!hasState) {
            const emptyState = GymScheduler.createEmptySessions(amountOfStations, gym);
            const state = new GymState(cycleTime, this.timeSlices.getFollowingCycle(cycleTime), emptyState);
            return gymSchedule.setState(cycleTime.getTime(), state)
              .then(() => resolve());
          }

          return resolve();
        })
        .catch(reject);
    });
  }

  private anonymizeSession(workoutSession: UserWorkoutSession, dateOfRequest: Date) {
    workoutSession.userProfileId = '';
    workoutSession.exerciseSessions = workoutSession.exerciseSessions.map((session: UserExerciseSession) => {
      session.type = ExerciseSessionType.Anonymous;
      return session;
    });
    this.checkinSession(workoutSession, dateOfRequest);
    workoutSession.anonymised = true;
  }

  private adjustedStartStationPosition(
    station: number,
    staticZoneType?: number,
  ) {
    if (staticZoneType) {
      return station;
    }
    return station - GymScheduler.stationsPerZone;
  }

  private adjustedStationPosition(
    lastStation: number,
    calculatedStation: number,
    staticZoneType?: number,
  ) {
    return staticZoneType ? lastStation : calculatedStation;
  }

  private checkinSession(workoutSession: UserWorkoutSession, dateOfRequest: Date) {
    workoutSession.checkedIn = true;
    workoutSession.gymCheckinTime = dateOfRequest;
  }
}
